type MessageHandler = (data: string) => void;
type StatusHandler = (status: 'connecting' | 'connected' | 'disconnected') => void;

const MOCK_ENABLED = import.meta.env.VITE_DEV_MOCK === '1';

export class TerminalSocket {
  private ws: WebSocket | null = null;
  private onMessage: MessageHandler;
  private onStatus: StatusHandler;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private manualClose = false;
  private buffer: string[] = [];
  private mockLine = '';
  // Persisted so reconnects re-apply the same workspace + cwd automatically.
  private projectId: string | null = null;
  private cwd: string | null = null;

  constructor(onMessage: MessageHandler, onStatus: StatusHandler) {
    this.onMessage = onMessage;
    this.onStatus = onStatus;
  }

  /** Remember the target workspace; applied on connect AND on reconnect. */
  setTargetWorkspace(projectId: string | null, cwd?: string | null) {
    this.projectId = projectId;
    this.cwd = cwd || null;
  }

  connect() {
    if (this.manualClose) return;
    if (MOCK_ENABLED) {
      // Local mock: echo a fake shell so the UI is explorable without a backend.
      this.onStatus('connecting');
      setTimeout(() => {
        this.onStatus('connected');
        this.onMessage(
          '\x1b[36mcloud-desktop (mock mode)\x1b[0m — no backend connected\r\n' +
            'Type a command and press Enter to see echo output.\r\n' +
            '\x1b[32mls\x1b[0m  \x1b[32mpwd\x1b[0m  \x1b[32mopencode\x1b[0m  \x1b[32mfreebuff\x1b[0m  \x1b[32mclear\x1b[0m\r\n\x1b[33m~/workspace\x1b[0m $ '
        );
      }, 400);
      return;
    }
    this.onStatus('connecting');
    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    // Spawn the shell directly in the right directory when possible.
    const cwdQs = this.cwd ? `?cwd=${encodeURIComponent(this.cwd)}` : '';
    const wsUrl = import.meta.env.VITE_WS_URL || `${protocol}${window.location.host}/ws/terminal${cwdQs}`;
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.onStatus('connected');
      this.reconnectAttempts = 0;
      // (Re)apply the workspace on every fresh connection — after a reconnect
      // a brand-new shell starts in its home dir, not the project folder.
      if (this.projectId || this.cwd) {
        this.sendJSON({
          type: 'workspace',
          projectId: this.projectId || undefined,
          path: this.cwd || undefined,
        });
      }
      // Flush any buffered data
      while (this.buffer.length > 0) {
        const data = this.buffer.shift();
        this.send(data!);
      }
    };

    this.ws.onmessage = (event) => {
      this.onMessage(event.data as string);
    };

    this.ws.onclose = () => {
      this.onStatus('disconnected');
      if (!this.manualClose) {
        this.attemptReconnect();
      }
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  send(data: string) {
    if (MOCK_ENABLED) {
      this.mockSend(data);
      return;
    }
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      this.buffer.push(data);
    }
  }

  private mockSend(data: string) {
    for (const ch of data) {
      if (ch === '\r' || ch === '\n') {
        const cmd = this.mockLine.trim();
        this.mockLine = '';
        this.handleMockCommand(cmd);
      } else if (ch === '\u007f' || ch === '\x08') {
        this.mockLine = this.mockLine.slice(0, -1);
      } else {
        this.mockLine += ch;
      }
    }
  }

  private handleMockCommand(cmd: string) {
    if (!cmd) {
      this.onMessage('~/workspace $ ');
      return;
    }
    if (cmd === 'clear') {
      this.onMessage('\x1b[2J\x1b[H\x1b[33m~/workspace\x1b[0m $ ');
      return;
    }
    if (cmd === 'ls' || cmd === 'ls -la') {
      this.onMessage('drwxr-xr-x  src\r\n-rw-r--r--  package.json\r\n-rw-r--r--  index.html\r\n');
    } else if (cmd === 'pwd') {
      this.onMessage('/workspace/mock-project-1\r\n');
    } else if (cmd === 'opencode' || cmd === 'freebuff') {
      this.onMessage(`\r\n\x1b[36m>>> ${cmd} (mock) — would launch the coding agent here\x1b[0m\r\n`);
    } else if (cmd.startsWith('cd ')) {
      this.onMessage(''); // silently accept
    } else {
      this.onMessage(`\x1b[90m(mock shell):\x1b[0m ${cmd}\r\n`);
    }
    this.onMessage('\x1b[33m~/workspace\x1b[0m $ ');
  }

  sendJSON(obj: object) {
    if (MOCK_ENABLED) return; // control messages are no-ops in mock mode
    this.send(JSON.stringify(obj));
  }

  sendResize(cols: number, rows: number) {
    this.sendJSON({ type: 'resize', cols, rows });
  }

  setWorkspace(path: string) {
    this.sendJSON({ type: 'workspace', path });
  }

  setProjectWorkspace(projectId: string, workspacePath?: string) {
    this.setTargetWorkspace(projectId, workspacePath);
    this.sendJSON({ type: 'workspace', projectId, path: workspacePath || undefined });
  }

  reconnect() {
    this.manualClose = false;
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.connect();
  }

  disconnect() {
    this.manualClose = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }
}